import { useQuery } from "@apollo/client";
import ChevronRight from "@mui/icons-material/ChevronRight";
import DeleteOutline from "@mui/icons-material/DeleteOutline";
import Maximize from "@mui/icons-material/Maximize";
import Minimize from "@mui/icons-material/Minimize";
import {
  Box,
  Button,
  IconButton,
  LinearProgress,
  List,
  ListItem,
  Skeleton,
  Stack,
  Typography,
} from "@mui/material";
import { MinimizableDialog } from "components/MinimizableDialog";
import {
  DocumentAiEnrichmentJobQuery,
  ExtractionStatus,
  JobStatus,
} from "generated-graphql/graphql";
import { terminalJobStatuses } from "hooks/useLongPollingForJob";
import { useTenant } from "hooks/useTenant";
import { useDocumentReview } from "providers/documentReview";
import { useMinimizedDialogs } from "providers/minimizedDialogs";
import { useCallback, useEffect, useMemo } from "react";
import { useNavigate } from "react-router-dom";

import { AnalysisRowStatus, AnalyzingRow, AnalyzingTitleBar } from "./AnalyzingDialog";
import { DOCUMENT_AI_ENRICHMENT_JOB_STATUS } from "./queries";

function rowStatusFromExtractionStatus(
  extractionStatus: ExtractionStatus | null | undefined
): AnalysisRowStatus {
  if (extractionStatus === ExtractionStatus.Succeeded) {
    return "done";
  }
  if (extractionStatus === ExtractionStatus.Failed) {
    return "failed";
  }
  return "inProgress";
}

/**
 * Whether the review action is offered. A failed run offers it only to a caller
 * that opted in, whose review surface is where retry lives.
 */
function canOpenReview({
  isSucceeded,
  isFailed,
  reviewOnFailure,
}: {
  isSucceeded: boolean;
  isFailed: boolean;
  reviewOnFailure: boolean;
}) {
  return isSucceeded || (reviewOnFailure && isFailed);
}

/**
 * The documents handed to `onReview`. A failed run has no succeeded documents,
 * so filtering by status would hand an opted-in caller an empty list and
 * nothing to open; those callers take the whole job instead.
 */
function reviewableDocumentIds(
  documents: readonly { id: string; extractionStatus?: ExtractionStatus | null }[],
  reviewOnFailure: boolean
): string[] {
  const eligible = reviewOnFailure
    ? documents
    : documents.filter((doc) => doc.extractionStatus === ExtractionStatus.Succeeded);
  return eligible.map((doc) => doc.id);
}

export function DocumentAIAnalyzingDialog({
  open,
  onClose,
  jobId,
  preferOriginalTitle = false,
  dialogId,
  onReview,
  onComplete,
  wholeBoxClickable = false,
  onDiscard,
  discardLabel,
  onCloseWithoutReview,
  reviewOnFailure = false,
}: {
  open: boolean;
  onClose:
    | (() => void)
    | ((
        event: React.MouseEvent<HTMLElement>,
        reason: "backdropClick" | "escapeKeyDown" | "cancelButtonClick" | "closeButtonClick"
      ) => void);
  jobId: string;
  /** Unique ID for this dialog instance (for minimization) */
  dialogId?: string;
  preferOriginalTitle?: boolean;
  /** Custom review action. Receives IDs of successfully extracted documents. When omitted, navigates to the Documents page. */
  onReview?: (documentIds: string[]) => void;
  /** Called when the job reaches a terminal status (succeeded or failed). */
  onComplete?: () => void;
  /**
   * When true, clicking anywhere on the minimized chip (not just the icon)
   * expands it. The icon's circular hover affordance is also dropped so the
   * whole-chip click is the primary affordance.
   */
  wholeBoxClickable?: boolean;
  /**
   * Opt-in destructive "bail out" action, shown only once analysis is terminal.
   * The owner is responsible for the confirm + deletion; this just renders the
   * button. Callers that don't pass it get no discard affordance.
   */
  onDiscard?: () => void;
  /** Label for the discard button. Defaults to "Discard upload". */
  discardLabel?: string;
  /**
   * Called when the user dismisses the dialog (cancel/backdrop/escape) after a
   * successful run WITHOUT choosing to review — i.e. the uploads are saved but
   * unreviewed. Lets the owner surface a reassurance toast. Not called on the
   * review-and-confirm path, on discard, or when nothing extracted successfully.
   */
  onCloseWithoutReview?: (info: { succeededCount: number }) => void;
  /**
   * Opt-in: let the reviewer open the review surface after a FAILED run, and
   * hand `onReview` every document in the job rather than only the ones that
   * extracted. Off by default, so the review button stays disabled on failure
   * for every caller that does not ask for this.
   *
   * For an owner whose review surface is also where retry lives: without it, a
   * run that fails has no route to the retry control, which is the one case
   * retry exists for.
   */
  reviewOnFailure?: boolean;
}) {
  const { minimize, expand, close, dialogs } = useMinimizedDialogs();
  const { setDocumentsToReview } = useDocumentReview();
  const { data, error, stopPolling } = useQuery(DOCUMENT_AI_ENRICHMENT_JOB_STATUS, {
    variables: { id: jobId },
    pollInterval: 1000,
  });

  useEffect(() => {
    if (
      error ||
      (data?.documentAIEnrichmentJob?.status &&
        terminalJobStatuses.includes(data.documentAIEnrichmentJob.status))
    ) {
      stopPolling();
      onComplete?.();
    }
  }, [
    data?.documentAIEnrichmentJob?.status,
    data?.documentAIEnrichmentJob?.documents,
    error,
    stopPolling,
    setDocumentsToReview,
    onComplete,
  ]);

  const { tenantId } = useTenant();
  const navigate = useNavigate();

  // Generate dialog ID if not provided
  const currentDialogId = dialogId || `document-ai-analyzing-${jobId}`;

  // Get dialog mode
  const dialogMode = dialogs.find((d) => d.id === currentDialogId)?.mode ?? "centered";

  // Get job state from query
  const status: JobStatus | undefined = data?.documentAIEnrichmentJob?.status;
  const isRunning =
    status === JobStatus.Queued ||
    status === JobStatus.Dispatched ||
    status === JobStatus.Running ||
    status === undefined; // initial state
  const isSucceeded = status === JobStatus.Succeeded;
  const isFailed = status === JobStatus.Failed;

  const analysisStats = useMemo(() => {
    const documents = data?.documentAIEnrichmentJob?.documents ?? [];
    const succeededDocuments = documents.filter(
      (document) => document.extractionStatus === ExtractionStatus.Succeeded
    ).length;
    return {
      totalDocuments: documents.length,
      succeededDocuments,
    };
  }, [data?.documentAIEnrichmentJob?.documents]);

  const progressCount = analysisStats.succeededDocuments;
  const totalCount = analysisStats.totalDocuments;

  const handleMinimize = useCallback(() => {
    minimize(currentDialogId);
  }, [currentDialogId, minimize]);

  const handleExpand = useCallback(() => {
    expand(currentDialogId);
  }, [currentDialogId, expand]);

  // The primary "Review & Confirm" button closes via close(dialogId) directly,
  // so any onClose here (cancel button / backdrop / escape) is a
  // dismiss-without-review. Notify the owner only when there's reviewable data
  // (terminal + at least one success) so it can reassure the user the uploads
  // are saved-but-unreviewed.
  const handleClose = useCallback(
    (
      event: React.MouseEvent<HTMLElement>,
      reason: "backdropClick" | "escapeKeyDown" | "cancelButtonClick" | "closeButtonClick"
    ) => {
      if (!isRunning && isSucceeded && progressCount > 0) {
        onCloseWithoutReview?.({ succeededCount: progressCount });
      }
      onClose(event, reason);
    },
    [isRunning, isSucceeded, progressCount, onCloseWithoutReview, onClose]
  );

  // Always render MinimizableDialog component, with mode prop
  return (
    <MinimizableDialog
      open={open}
      onClose={handleClose}
      fullWidth
      maxWidth="sm"
      mode={dialogMode}
      wholeBoxClickable={wholeBoxClickable}
      onMinimizedClick={handleExpand}
      title={
        <DocumentAIAnalyzingTitle
          progressCount={progressCount}
          totalCount={totalCount}
          isFailed={isFailed}
          isRunning={isRunning}
          isSucceeded={isSucceeded}
        />
      }
      hideCloseButton={true}
      additionalPrimaryActionElements={
        onDiscard && !isRunning
          ? [
              <Button
                key="discard"
                variant="text"
                color="error"
                size="medium"
                startIcon={<DeleteOutline />}
                onClick={onDiscard}
              >
                {discardLabel ?? "Discard upload"}
              </Button>,
            ]
          : undefined
      }
      titleBarActionElement={
        <IconButton
          onClick={(e) => {
            // When the whole minimized box is clickable, the paper-level handler
            // also fires; stop propagation so we don't expand twice.
            if (wholeBoxClickable && dialogMode === "minimizedInTray") {
              e.stopPropagation();
            }
            if (dialogMode === "minimizedInTray") {
              handleExpand();
            } else {
              handleMinimize();
            }
          }}
          // Drop the circular hover affordance when the whole chip is clickable;
          // the icon is then a hint, not the primary affordance.
          disableRipple={wholeBoxClickable && dialogMode === "minimizedInTray"}
          sx={
            wholeBoxClickable && dialogMode === "minimizedInTray"
              ? { "&:hover": { backgroundColor: "transparent" } }
              : undefined
          }
        >
          {dialogMode === "minimizedInTray" ? <Maximize /> : <Minimize />}
        </IconButton>
      }
      primaryActionButton={
        isRunning ? (
          <Button onClick={handleMinimize} variant="text" size="medium">
            Minimize
          </Button>
        ) : (
          <Button
            disabled={!canOpenReview({ isSucceeded, isFailed, reviewOnFailure })}
            onClick={() => {
              const documentIds = reviewableDocumentIds(
                data?.documentAIEnrichmentJob.documents ?? [],
                reviewOnFailure
              );

              if (onReview) {
                onReview(documentIds);
              } else if (documentIds.length > 0 && tenantId) {
                setDocumentsToReview(documentIds);
                navigate(`/o/${tenantId}/documents`);
              }
              close(currentDialogId);
            }}
            variant="text"
            size="medium"
            endIcon={isSucceeded ? <ChevronRight /> : undefined}
          >
            {isSucceeded && progressCount === 0 ? "Exit" : "Review & Confirm Details"}
          </Button>
        )
      }
      hideCancelButton={isRunning}
      cancelButtonProps={{ text: "Close" }}
    >
      <Stack spacing={2}>
        <Box>
          <LinearProgress
            value={isRunning && totalCount > 0 ? (progressCount / totalCount) * 100 : 100}
            variant={isRunning && progressCount === 0 ? "indeterminate" : "determinate"}
          />

          <Typography variant="body2" color="text.secondary" sx={{ mt: 1 }}>
            {isRunning &&
              "Hang tight, we're reading your uploads and filling in what we can. This usually takes about 5-10 seconds per file, though larger files may take longer. We'll let you know when everything is ready for you to review."}
            {isSucceeded &&
              `Your documents have been uploaded and we have filled in as much information as we could. When you have time, please review document classifications and details for each upload.`}
            {isFailed && "Analysis failed."}
          </Typography>
        </Box>

        <List dense>
          {totalCount > 0 ? (
            data?.documentAIEnrichmentJob?.documents.map((documentRecord) => {
              return (
                <DocumentAIAnalyzingRow
                  key={documentRecord.id}
                  documentRecord={documentRecord}
                  preferOriginalTitle={preferOriginalTitle}
                />
              );
            })
          ) : (
            <ListItem>
              <Skeleton variant="text" width="100%" />
            </ListItem>
          )}
        </List>
      </Stack>
    </MinimizableDialog>
  );
}

function DocumentAIAnalyzingTitle({
  progressCount,
  totalCount,
  isFailed,
  isRunning,
  isSucceeded,
}: {
  progressCount: number;
  totalCount: number;
  isFailed: boolean;
  isRunning: boolean;
  isSucceeded: boolean;
}) {
  if (totalCount === 0) {
    return <Skeleton variant="text" width="50%" />;
  }

  return (
    <AnalyzingTitleBar
      noun="Document"
      count={totalCount}
      progressCount={progressCount}
      totalCount={totalCount}
      isFailed={isFailed}
      isRunning={isRunning}
      isSucceeded={isSucceeded}
    />
  );
}

function DocumentAIAnalyzingRow({
  documentRecord,
  preferOriginalTitle = false,
}: {
  documentRecord: DocumentAiEnrichmentJobQuery["documentAIEnrichmentJob"]["documents"][number];
  preferOriginalTitle?: boolean;
}) {
  const label = preferOriginalTitle
    ? (documentRecord.originalTitle ?? documentRecord.title)
    : documentRecord.title;

  return (
    <AnalyzingRow
      label={label}
      status={rowStatusFromExtractionStatus(documentRecord.extractionStatus)}
      error={documentRecord.extractionError}
    />
  );
}
